/**
 * Highlights differences between two strings using the LCS (longest common subsequence) algorithm.
 *
 * Algorithm:
 * 1. Build a dynamic programming (DP) matrix to calculate the LCS between the database string and the external string.
 * 2. Perform backtracking from the end of the DP matrix to reconstruct the "diff":
 *    - If characters match → add them as normal (no highlight).
 *    - If a character exists in the database but not in the external string → mark it in red (missing).
 *    - If a character exists in the external string but not in the database → mark it in red (extra).
 *
 * Every difference will always be highlighted in red.
 */
export function highlighting_differences(info_db: string = "", info_external: string = ""): string {
  const isIsoDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s.trim());

  // If both are ISO dates (YYYY-MM-DD), compare tokens year/month/day
  if (isIsoDate(info_db) && isIsoDate(info_external)) {
    const [year_db, month_db, day_db] = info_db.trim().split("-");
    const [year_ex, month_ex, day_ex] = info_external.trim().split("-");

    const token = (external: string, db: string) =>
      external === db ? escape_html(external) : `<span class="text-danger">${escape_html(external)}</span>`;

    return `${token(year_ex, year_db)}-${token(month_ex, month_db)}-${token(day_ex, day_db)}`;
  }

  const db_length = info_db.length;
  const external_length = info_external.length;

  // Step 1: Build DP matrix for LCS calculation
  const lcs_matrix: number[][] = Array.from({ length: db_length + 1 }, () => Array(external_length + 1).fill(0));

  for (let row = 1; row <= db_length; row++) {
    for (let col = 1; col <= external_length; col++) {
      lcs_matrix[row][col] =
        info_db[row - 1] === info_external[col - 1]
          ? lcs_matrix[row - 1][col - 1] + 1 // Characters match → extend LCS
          : Math.max(lcs_matrix[row - 1][col], lcs_matrix[row][col - 1]); // Take the longest possible subsequence
    }
  }

  // Step 2: Backtracking to reconstruct the diff output
  const output_parts: string[] = [];
  let index_db = db_length;
  let index_external = external_length;

  while (index_db > 0 || index_external > 0) {
    const db_char = index_db > 0 ? info_db[index_db - 1] : "";
    const external_char = index_external > 0 ? info_external[index_external - 1] : "";

    // Characters match → no difference
    if (index_db > 0 && index_external > 0 && db_char === external_char) {
      output_parts.push(escape_html(db_char));
      index_db--;
      index_external--;
    }
    // Character present in DB but missing in external string → highlight in red
    else if (
      index_db > 0 &&
      (index_external === 0 || lcs_matrix[index_db - 1][index_external] >= lcs_matrix[index_db][index_external - 1])
    ) {
      output_parts.push(`<del class="text-danger">${escape_html(db_char)}</del>`);
      index_db--;
    } else if (index_external > 0) {
      output_parts.push(`<strong class="text-danger">${escape_html(external_char)}</strong>`);
      index_external--;
    }
  }

  // Reverse the result because backtracking builds it backwards
  return output_parts.reverse().join("");
}

/**
 * Escapes HTML characters to avoid injection issues.
 */
function escape_html(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
